import { makeAutoObservable, runInAction } from 'mobx';
import { RsValue } from '../../models/base/BaseModel';
import { LoginResponse, LoginRequest } from '../../models/user/UserModels';
import { authService } from '../../services/auth.service';
import JwtHelper from '../../types/JwtHelper';
import { RootStore } from '../RootStore';
import { ForgotPasswordRequest, ResetPasswordRequest } from './UserModels';

class AuthStore {
  rootStore: RootStore;
  token: string | null;
  user: LoginResponse | null;
  isAuthenticated: boolean = false;
  error: string | null = null;
  userRoles: string[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    const storedToken = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken');
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');

    this.token = storedToken;
    try {
      this.user = storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      this.user = null;
    }

    this.isAuthenticated = this.checkAuth();

    if (this.token) {
      this.userRoles = this.token ? JwtHelper.getRoles(this.token) : [];
    }

    makeAutoObservable(this, {}, { autoBind: true });
  }

  checkAuth = (): boolean => {
    try {
      return this.token !== null && this.user !== null && !JwtHelper.isTokenExpired(this.token);
    } catch (e) {
      console.error('Error checking token expiration:', e);
      return false;
    }
  };

  hasRole = (role: string): boolean => {
    return this.userRoles.includes(role);
  };

  hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => this.userRoles.includes(role));
  };

  get isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  login = async (
    credentials: LoginRequest,
    rememberMe: boolean = false
  ): Promise<RsValue<LoginResponse>> => {
    this.error = null;
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: RsValue<LoginResponse> = await authService.login(credentials);
      if (!response.error && response.value) {
        const { jwtToken, ...userData } = response.value;
        runInAction(() => {
          this.setToken(jwtToken, rememberMe);
          this.setUser(response.value, rememberMe);
          this.isAuthenticated = this.checkAuth();
        });
        this.rootStore.uiStore.setLoading(false);
        return response;
      } else {
        runInAction(() => {
          this.error = response.message;
          this.isAuthenticated = false;
        });
        this.rootStore.uiStore.setLoading(false);
        return response;
      }
    } catch (error) {
      this.rootStore.uiStore.setLoading(false);
      runInAction(() => {
        const errorResponse: RsValue<LoginResponse> = {
          error: true,
          message: 'Giriş işlemi sırasında bir hata oluştu',
          statusCode: 500,
          value: null,
        };
        this.error = errorResponse.message;
        this.isAuthenticated = false;
        console.error('Login failed:', error);
      });

      const errorResponse: RsValue<LoginResponse> = {
        error: true,
        message: 'Giriş işlemi sırasında bir hata oluştu',
        statusCode: 500,
        value: null,
      };
      this.rootStore.uiStore.setLoading(false);
      return errorResponse;
    }
  };

  setToken = (token: string, rememberMe: boolean = false) => {
    if (rememberMe) {
      localStorage.setItem('jwtToken', token);
      sessionStorage.removeItem('jwtToken');
    } else {
      sessionStorage.setItem('jwtToken', token);
      localStorage.removeItem('jwtToken');
    }
    this.token = token;
    this.userRoles = token ? JwtHelper.getRoles(token) : []; // Token null ise rolleri boş dizi yap
  };

  setUser = (user: LoginResponse | null, rememberMe: boolean = false) => {
    if (user) {
      if (rememberMe) {
        localStorage.setItem('user', JSON.stringify(user));
        sessionStorage.removeItem('user');
      } else {
        sessionStorage.setItem('user', JSON.stringify(user));
        localStorage.removeItem('user');
      }
    } else {
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
    }
    this.user = user;
  };

  logout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('jwtToken');
    sessionStorage.removeItem('user');
    runInAction(() => {
      // MobX state güncellemeleri için runInAction kullan
      this.token = null;
      this.user = null;
      this.isAuthenticated = false;
      this.userRoles = [];
    });
  };
  forgotPassword = async (payload: ForgotPasswordRequest): Promise<RsValue<string>> => {
    this.error = null;
    try {
      const response: RsValue<string> = await authService.forgotPassword(payload);

      if (!response.error) {
        return response;
      } else {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
    } catch (error) {
      runInAction(() => {
        const errorResponse: RsValue<string> = {
          error: true,
          message: 'Şifre sıfırlama işlemi sırasında bir hata oluştu',
          statusCode: 500,
          value: null,
        };
        this.error = errorResponse.message;
        console.error('ForgotPassword failed:', error);
      });

      const errorResponse: RsValue<string> = {
        error: true,
        message: 'Şifre sıfırlama işlemi sırasında bir hata oluştu',
        statusCode: 500,
        value: null,
      };
      return errorResponse;
    }
  };

  /**
   * Reset password isteği gönderir.
   * @param payload.email Kullanıcının e-posta adresi
   * @param payload.token  Şifre sıfırlama e-postasından gelen token
   * @param payload.newPassword  Kullanıcının belirlediği yeni şifre
   */
  resetPassword = async (payload: ResetPasswordRequest): Promise<RsValue<string>> => {
    this.error = null;
    try {
      // authService.resetPassword, backend'deki /User/reset-password endpoint'ine POST isteği atar
      const response: RsValue<string> = await authService.resetPassword(payload);

      if (!response.error) {
        return response;
      } else {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }
    } catch (error) {
      runInAction(() => {
        const errorResponse: RsValue<string> = {
          error: true,
          message: 'Şifre yenileme işlemi sırasında bir hata oluştu',
          statusCode: 500,
          value: null,
        };
        this.error = errorResponse.message;
        console.error('ResetPassword failed:', error);
      });

      const errorResponse: RsValue<string> = {
        error: true,
        message: 'Şifre yenileme işlemi sırasında bir hata oluştu',
        statusCode: 500,
        value: null,
      };
      return errorResponse;
    }
  };

  get userId() {
    return this.user?.id;
  }

  get username() {
    return this.user?.username;
  }
}

export default AuthStore;
